'use strict';

import Swiper from 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.mjs';

/* ─────────────────────────────────────────────
   Cart state reader
   ───────────────────────────────────────────── */

/**
 * Build the current cart state from:
 *   - Hidden `.promo-line-item` nodes rendered inside the component by Liquid
 *   - A fallback to /cart.js for the authoritative subtotal
 *
 * The Liquid snippet renders:
 *   <div class="promo-line-item"
 *        data-variant-id="…"
 *        data-tags="tag1,tag2,…"
 *        data-quantity="2"
 *        style="display:none">
 *   </div>
 *
 * @param {HTMLElement} _hostElement  Unused — kept for API compatibility
 * @param {string} [tag]             Product tag to count (for xplus1)
 * @param {AbortSignal} [signal]
 * @returns {Promise<CartState>}
 */
async function getCartState(_hostElement, tag, signal) {
  // Read subtotal from /cart.js (authoritative, always in cents)
  const cartResponse = await fetch('/cart.js', { signal, credentials: 'same-origin' });
  if (!cartResponse.ok) {
    throw new Error(`/cart.js error: ${cartResponse.status}`);
  }
  const cartJson = await cartResponse.json();
  const subtotal = (cartJson.total_price ?? 0) / 100;
  const activeCurrency = cartJson.currency ?? 'EUR';

  // Count tagged-product quantity and subtotal from window.cartPromoLineItems (set by Liquid)
  // item.price is in cents (Shopify convention), divided by 100 here
  let taggedProductQty = 0;
  let taggedProductSubtotal = 0;
  if (tag) {
    const lineItems = window.cartPromoLineItems || [];
    for (const item of lineItems) {
      const tags = (item.tags || []).map((t) => String(t).trim().toLowerCase());
      if (tags.includes(tag)) {
        taggedProductQty += item.quantity || 0;
        taggedProductSubtotal += ((item.price || 0) * (item.quantity || 0)) / 100;
      }
    }
  }

  return { subtotal, taggedProductQty, taggedProductSubtotal, activeCurrency };
}

/**
 * Add a single variant to the cart via AJAX.
 *
 * @param {number} variantId
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
async function addToCart(variantId, signal) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 }),
    credentials: 'same-origin',
    signal,
  });
  if (!response.ok) throw new Error(`/cart/add.js error: ${response.status}`);
  return response.json();
}

/* ─────────────────────────────────────────────
   Web Component
   ───────────────────────────────────────────── */

class CartPromotionGoals extends HTMLElement {
  constructor() {
    super();
    /** @type {AbortController|null} */
    this._abortController = null;
    this._swiper = null;
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    // Listen for cart updates using the theme's native events
    document.addEventListener('cart:change', this._onCartChange.bind(this), { signal });
    document.addEventListener('cart:refresh', this._onCartRefresh.bind(this), { signal });
    document.documentElement.addEventListener('cart:change', this._onCartChange.bind(this), { signal });
    // Handle gift product add-to-cart clicks
    this.addEventListener('click', this._onGiftCtaClick.bind(this), { signal });

    this._render();
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._destroySwiper();
  }

  /* ── Event handlers ── */

  _onGiftCtaClick(event) {
    const btn = event.target.closest('.cart-promotion-goal__gift-add[data-variant-id]');
    if (!btn || btn.hasAttribute('data-loading')) return;
    event.preventDefault();
    this._addGiftToCart(btn);
  }

  async _addGiftToCart(btn) {
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    btn.setAttribute('data-loading', '');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    const isInDrawer = !!this.closest('cart-drawer-custom');
    try {
      await addToCart(variantId, this._abortController?.signal);
      if (isInDrawer) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } else {
        window.location.reload();
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[cart-promotion-goals] addToCart failed', err);
      window.location.href = btn.href;
    } finally {
      btn.removeAttribute('data-loading');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  _onCartChange(event) {
    // The theme passes the updated cart object in event.detail.cart
    // We still re-render so the Liquid data-tags nodes are up-to-date
    // (the cart drawer re-renders the section HTML which re-connects this element)
    this._render();
  }

  _onCartRefresh() {
    this._render();
  }

  /* ── Core render pipeline ── */

  async _render() {
    const signal = this._abortController?.signal;
    if (!signal || signal.aborted) return;

    try {
      const blocks = [...this.querySelectorAll('.cart-promotion-goal')];

      if (blocks.length === 0) return;

      // Gather unique product tags needed for xplus1 and promo_product promos
      const tags = [
        ...new Set(
          blocks
            .filter((b) => (b.dataset.promoType === 'xplus1' || b.dataset.promoType === 'promo_product') && b.dataset.productTag)
            .map((b) => b.dataset.productTag.trim().toLowerCase())
        ),
      ];

      const cartState = await getCartState(this, tags[0] ?? null, signal);

      this._renderGoals(blocks, cartState);

      if (!this.querySelector('.cart-promotion-goal')) {
        this.setAttribute('hidden', '');
        this._destroySwiper();
        return;
      }

      this.removeAttribute('hidden');
      this._syncSwiper();
    } catch (err) {
      if (err.name === 'AbortError') return; // Component was disconnected
      console.error('[cart-promotion-goals]', err);
    }
  }

  /* ── DOM rendering ── */

  _renderGoals(blocks, cartState) {
    const activeCurrency = cartState.activeCurrency;

    for (const block of blocks) {
      const type = block.dataset.promoType;
      const thresholdQty = block.dataset.thresholdQty ? parseInt(block.dataset.thresholdQty, 10) : undefined;
      const thresholdAmount = block.dataset.thresholdAmount ? parseFloat(block.dataset.thresholdAmount) : undefined;
      const progressMessageTemplate = block.dataset.progressMessage || '';
      const giftMessage = block.dataset.giftMessage || '';

      const { remaining, completed } = computeProgress(
        { type, thresholdQty, thresholdAmount },
        cartState
      );

      const inner = block.querySelector('.cart-promotion-goal__inner');
      const msgEl = block.querySelector('.cart-promotion-goal__message');
      const progressCta = block.querySelector('.cart-promotion-goal__cta');
      const giftCta = block.querySelector('.cart-promotion-goal__gift-cta');
      const giftAddCta = block.querySelector('.cart-promotion-goal__gift-add');
      const productCard = block.querySelector('.cart-promotion-goal__product-card');

      if (completed && giftMessage) {
        // Check if the gift product is already in the cart
        const giftVariantEl = block.querySelector('.promo-line-item[data-variant-id]');
        const giftVariantId = giftVariantEl ? parseInt(giftVariantEl.dataset.variantId, 10) : null;
        const giftAlreadyInCart = giftVariantId
          ? (window.cartPromoLineItems || []).some((item) => item.variantId === giftVariantId)
          : false;

        if (giftAlreadyInCart) {
          // Gift already in cart: hide the block entirely
          block.remove();
        } else {
          // Threshold reached and a gift message is configured: switch to gift state
          inner?.classList.add('cart-promotion-goal__inner--gift');
          if (msgEl) msgEl.textContent = giftMessage;
          if (progressCta) progressCta.setAttribute('hidden', '');
          if (giftCta) giftCta.removeAttribute('hidden');
          if (giftAddCta) giftAddCta.removeAttribute('hidden');
          if (productCard) productCard.removeAttribute('hidden');
        }
      } else if (completed) {
        // Threshold reached, no gift message: remove block entirely
        block.remove();
      } else {
        // Progress state (also handles reverting from gift state if items are removed)
        inner?.classList.remove('cart-promotion-goal__inner--gift');
        const message = buildMessage(progressMessageTemplate, remaining, type, activeCurrency);
        if (msgEl) msgEl.textContent = message;
        if (progressCta) progressCta.removeAttribute('hidden');
        if (giftCta) giftCta.setAttribute('hidden', '');
        if (giftAddCta) giftAddCta.setAttribute('hidden', '');
        if (productCard) productCard.setAttribute('hidden', '');
      }
    }
  }

  _syncSwiper() {
    const slides = this.querySelectorAll('.cart-promotion-goal');
    const pagination = this.querySelector('.swiper-pagination');

    if (slides.length <= 1) {
      this.style.setProperty('--cart-promo-pagination-width', '0px');
      this.style.setProperty('--cart-promo-column-gap', '0px');
      pagination?.setAttribute('hidden', '');
      this._destroySwiper();
      return;
    }

    this.style.removeProperty('--cart-promo-pagination-width');
    this.style.removeProperty('--cart-promo-column-gap');
    pagination?.removeAttribute('hidden');

    if (!this._swiper) {
      const viewport = this.querySelector('.cart-promotion-goals__viewport');

      if (!viewport) return;

      this._swiper = new Swiper(viewport, {
        slidesPerView: 1,
        loop: true,
        speed: 500,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        pagination: {
          el: pagination,
          clickable: true,
        },
      });
    }

    this._swiper.update();
  }

  _destroySwiper() {
    this._swiper?.destroy(true, true);
    this._swiper = null;
  }

}


/* ─────────────────────────────────────────────
   Progress computation
   ───────────────────────────────────────────── */

/**
 * @param {PromotionConfig} promo
 * @param {CartState} cartState
 * @returns {{ percent: number, remaining: number|string, completed: boolean }}
 */
function computeProgress(promo, cartState) {
  if (promo.type === 'xplus1') {
    const current = cartState.taggedProductQty;
    const threshold = promo.thresholdQty;
    const completed = current >= threshold;
    const remaining = Math.max(threshold - current, 0);
    const percent = Math.min((current / threshold) * 100, 100);
    return { percent, remaining, completed };
  }

  if (promo.type === 'free_shipping') {
    const current = cartState.subtotal;
    const threshold = promo.thresholdAmount;
    const completed = current >= threshold;
    const remaining = Math.max(threshold - current, 0);
    const percent = Math.min((current / threshold) * 100, 100);
    return { percent, remaining, completed };
  }

  if (promo.type === 'promo_product') {
    const current = cartState.taggedProductSubtotal;
    const threshold = promo.thresholdAmount;
    const completed = current >= threshold;
    const remaining = Math.max(threshold - current, 0);
    const percent = Math.min((current / threshold) * 100, 100);
    return { percent, remaining, completed };
  }

  return { percent: 0, remaining: 0, completed: false };
}

/**
 * Replace `{{remaining}}` placeholder in the message template.
 * For free_shipping promos, formats the remaining amount as a locale-aware currency string.
 * For other promos (e.g. xplus1), outputs the raw number.
 *
 * @param {string} template
 * @param {number} remaining
 * @param {'xplus1'|'free_shipping'} promoType
 * @param {string} activeCurrency  ISO 4217 currency code, e.g. 'EUR'
 * @returns {string}
 */
function buildMessage(template, remaining, promoType, activeCurrency) {
  let formatted;
  if (promoType === 'free_shipping' || promoType === 'promo_product') {
    try {
      formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: activeCurrency,
      }).format(remaining);
    } catch {
      // Fallback if currency code is invalid
      formatted = `${activeCurrency} ${remaining.toFixed(2)}`;
    }
  } else {
    formatted = String(remaining);
  }
  return template.replace(/\{\{\s*remaining\s*\}\}/g, formatted);
}

/* ─────────────────────────────────────────────
   Registration
   ───────────────────────────────────────────── */

if (!customElements.get('cart-promotion-goals')) {
  customElements.define('cart-promotion-goals', CartPromotionGoals);
}

/* ─────────────────────────────────────────────
   JSDoc type definitions (no runtime cost)
   ───────────────────────────────────────────── */

/**
 * @typedef {Object} CartState
 * @property {number} subtotal           Cart subtotal in currency units (not cents)
 * @property {number} taggedProductQty      Total quantity of items matching the promo tag
 * @property {number} taggedProductSubtotal  Subtotal (in currency units) of items matching the promo tag
 * @property {string} activeCurrency         ISO 4217 currency code
 */
